<script setup>
import { ref, computed } from 'vue'
import { API, HOST } from '@/utils/http'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { toast } from '@/components/ui/toast'
import { Download } from 'lucide-vue-next'

const props = defineProps({
  orgId: {
    type: [String, Number],
    required: true
  },
  layerCode: {
    type: String,
    required: true
  },
  dateRange: {
    type: Array,
    default: () => null
  }
})

const isOpen = ref(false)
const isExporting = ref(false)

// Export options
const exportFormat = ref('csv')
const dateFormat = ref('readable')
const groupByTemplate = ref(false)

const isCSV = computed(() => exportFormat.value === 'csv')

const open = () => {
  isOpen.value = true
}

const close = () => {
  isOpen.value = false
}

const exportData = async () => {
  if (!props.layerCode) return

  isExporting.value = true
  try {
    let url = `${HOST}/org/${props.orgId}/layers/${props.layerCode}/export`
    const params = {}

    if (props.dateRange && Array.isArray(props.dateRange) && props.dateRange.length >= 2) {
      params.startDate = props.dateRange[0]
      params.endDate = props.dateRange[1]
    }

    const response = await API({
      method: 'POST',
      url: url,
      params: params,
      data: {
        options: {
          format: exportFormat.value,
          flatten_custom_data: true,
          include_member_info: true,
          include_resources: 'combined',
          date_format: dateFormat.value,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
          group_by_template: groupByTemplate.value
        },
        use_layer_filter: true
      },
      responseType: 'blob'
    })

    // Detect response type from Content-Type header
    const contentType = response.headers?.['content-type'] || ''
    const isZip = contentType.includes('application/zip')
    const isGeoJSON = exportFormat.value === 'geojson'

    let defaultExt, mimeType
    if (isZip) {
      defaultExt = 'zip'
      mimeType = 'application/zip'
    } else if (isGeoJSON) {
      defaultExt = 'geojson'
      mimeType = 'application/geo+json'
    } else {
      defaultExt = 'csv'
      mimeType = 'text/csv'
    }

    // Get filename from Content-Disposition header or create default
    let filename = `features_export.${defaultExt}`
    if (response.headers && response.headers['content-disposition']) {
      const contentDisposition = response.headers['content-disposition']
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/)
      if (filenameMatch) {
        filename = filenameMatch[1]
      }
    }

    // If no filename in headers, create one with timestamp
    if (filename === `features_export.${defaultExt}`) {
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '').replace('T', '_')
      filename = `features_export_${timestamp}.${defaultExt}`
    }

    // Create blob and download
    const blob = new Blob([response.data], { type: mimeType })
    const downloadUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = downloadUrl
    link.download = filename
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(downloadUrl)

    toast({
      title: 'Export successful',
      description: `${filename} downloaded successfully`,
      variant: 'default'
    })

    close()

  } catch (error) {
    console.error('Error exporting data:', error)
    toast({
      title: 'Export failed',
      description: 'Failed to export data',
      variant: 'destructive'
    })
  } finally {
    isExporting.value = false
  }
}

defineExpose({
  open
})
</script>

<template>
  <Dialog :open="isOpen" @update:open="isOpen = $event">
    <DialogContent class="max-w-[400px]">
      <DialogHeader>
        <DialogTitle>Export Data</DialogTitle>
        <DialogDescription>
          Configure export options for layer features
        </DialogDescription>
      </DialogHeader>

      <div class="space-y-5 py-4">
        <!-- Format Selection -->
        <div class="space-y-2">
          <Label>Export Format</Label>
          <Select v-model="exportFormat">
            <SelectTrigger>
              <SelectValue placeholder="Select format" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="csv">CSV (Spreadsheet)</SelectItem>
              <SelectItem value="geojson">GeoJSON (Map data)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <!-- Date Format -->
        <div class="space-y-2">
          <Label>Date Format</Label>
          <Select v-model="dateFormat">
            <SelectTrigger>
              <SelectValue placeholder="Select date format" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="readable">Readable (January 15, 2024 10:30 AM)</SelectItem>
              <SelectItem value="iso">ISO (2024-01-15T10:30:00Z)</SelectItem>
              <SelectItem value="date_only">Date Only (2024-01-15)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <!-- Group by Template (CSV only) -->
        <div v-if="isCSV" class="flex items-center justify-between">
          <div>
            <Label>Group by form type</Label>
            <p class="text-xs text-muted-foreground mt-0.5">
              Separate CSV per form type in a ZIP file
            </p>
          </div>
          <Switch :model-value="groupByTemplate" @update:model-value="groupByTemplate = $event" />
        </div>

      </div>

      <DialogFooter>
        <Button type="button" variant="ghost" @click="close">Cancel</Button>
        <Button
          type="button"
          @click="exportData"
          :disabled="isExporting"
          class="bg-primary text-white hover:bg-primary/90"
        >
          <Download class="h-4 w-4 mr-2" />
          <span v-if="isExporting">Exporting...</span>
          <span v-else>Export</span>
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
